/**
 * The interaction timeline: everything that has happened with this contact,
 * newest first, filterable by type and date range.
 *
 * **Paged, not infinite scroll**, with an explicit «نمایش بیشتر». Three
 * reasons, recorded in docs/decisions.md: the timeline shares a scroll
 * container with the rest of the profile, so an infinite list would hijack the
 * page's scrollbar; a filtered view usually fits on one page, making automatic
 * loading invisible work; and a back-office user reading history wants to know
 * how much there is, which a total count and a button tell them and an endless
 * list does not.
 *
 * Attachment links are minted per click, never rendered as hrefs — a presigned
 * URL expires in minutes, so a link built at render time would be dead by the
 * time anyone clicked it.
 */
import { useQuery } from '@apollo/client';
import { Button, Card, Spinner } from '@meshhub/design-tokens/ui';
import { CalendarClock, CalendarPlus, Paperclip } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TIMELINE_PAGE_SIZE } from '../../constants/density.js';
import {
  INTERACTION_STATUS_LABELS,
  formatBytes,
  formatCount,
  formatDateTime,
  formatRelative,
  isoDateAndTimeToInstant,
} from '../../lib/format.js';
import { openAttachment } from '../../lib/files.js';
import { INTERACTIONS_QUERY } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { JalaliDateField, SelectField } from '../FormKit.web.jsx';

const NO_FILTERS = { typeId: '', dateFrom: null, dateTo: null };

/**
 * @param onFollowUp  called with an interaction when the user asks to plan a
 *   follow-up for it. Passed in rather than handled here so the profile owns the
 *   dialog — the timeline's job is to show what happened, not to schedule.
 */
export function Timeline({ contactId, types, reloadToken = 0, onFollowUp }) {
  const [filters, setFilters] = useState(NO_FILTERS);
  const [pageSize, setPageSize] = useState(TIMELINE_PAGE_SIZE);

  const { data, loading, error, refetch } = useQuery(INTERACTIONS_QUERY, {
    variables: {
      contactId,
      page: 1,
      pageSize,
      typeId: filters.typeId || null,
      dateFrom: filters.dateFrom ? isoDateAndTimeToInstant(filters.dateFrom, '00:00') : null,
      // Inclusive to the end of the chosen day, which is what «تا ۸ شهریور»
      // means to a person reading a date range.
      dateTo: filters.dateTo ? isoDateAndTimeToInstant(filters.dateTo, '23:59') : null,
    },
    fetchPolicy: 'cache-and-network',
  });

  // The parent bumps `reloadToken` after recording an interaction. A token
  // rather than a callback ref because the composer finishes *after* its file
  // uploads, and the parent is the only place that knows the whole flow is done.
  useEffect(() => {
    if (reloadToken > 0) refetch();
  }, [reloadToken, refetch]);

  const interactions = data?.interactions?.items ?? [];
  const total = data?.interactions?.totalCount ?? 0;
  const hasMore = interactions.length < total;
  const filtered = filters.typeId || filters.dateFrom || filters.dateTo;

  const set = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPageSize(TIMELINE_PAGE_SIZE); // a new filter starts at the first page
  };

  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-md font-bold">{S.contacts.detail.interactions}</h2>
        <span className="text-sm text-text-muted">{S.contacts.count(formatCount(total))}</span>
      </div>

      <div className="mb-5 grid grid-cols-4 items-end gap-3">
        <SelectField
          label={S.interaction.filterType}
          value={filters.typeId}
          onChange={(event) => set({ typeId: event.target.value })}
        >
          <option value="">{S.app.all}</option>
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.labelFa}
            </option>
          ))}
        </SelectField>

        <JalaliDateField
          label={S.interaction.filterFrom}
          value={filters.dateFrom}
          onChange={(iso) => set({ dateFrom: iso })}
        />
        <JalaliDateField
          label={S.interaction.filterTo}
          value={filters.dateTo}
          onChange={(iso) => set({ dateTo: iso })}
        />

        <Button
          variant="ghost"
          disabled={!filtered}
          onClick={() => set(NO_FILTERS)}
          className="justify-self-start"
        >
          {S.interaction.clearFilters}
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-base text-danger">
          {error.message}
        </p>
      )}

      {loading && interactions.length === 0 && (
        <div className="flex justify-center p-7">
          <Spinner />
        </div>
      )}

      {!loading && interactions.length === 0 && (
        <p className="text-text-muted">{S.contacts.detail.noInteractions}</p>
      )}

      <ol className="flex flex-col gap-5">
        {interactions.map((interaction) => (
          <li key={interaction.id} className="border-s-2 border-border ps-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <CalendarClock size={14} aria-hidden="true" className="text-text-muted" />
              <span className="font-medium">{interaction.type.labelFa}</span>
              <span className="text-sm text-text-muted">
                {formatRelative(interaction.occurredAt)}
              </span>
              <span className="ms-auto text-xs text-text-muted">
                {INTERACTION_STATUS_LABELS[interaction.status]}
              </span>
            </div>

            {interaction.description && (
              <p className="mt-1 whitespace-pre-line text-base">{interaction.description}</p>
            )}

            {interaction.attachments.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {interaction.attachments.map((file) => (
                  <li key={file.id}>
                    <button
                      type="button"
                      onClick={() => openAttachment(file.id)}
                      title={`${file.fileName} — ${formatBytes(file.sizeBytes)}`}
                      className="flex items-center gap-1 rounded border border-border px-3 py-1
                                 text-xs text-text-muted hover:bg-surface-hover hover:text-text"
                    >
                      <Paperclip size={12} aria-hidden="true" />
                      {file.fileName}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {interaction.outcome && (
              <p className="mt-1 text-sm text-text-muted">
                {S.contacts.detail.outcome}: {interaction.outcome}
              </p>
            )}

            <p className="mt-1 text-xs text-text-muted">
              {formatDateTime(interaction.occurredAt)} — {S.interaction.by}{' '}
              {interaction.createdByName}
            </p>

            {/*
              Step 4 closes Step 3's deferral: the follow-up NOTE that used to
              ride in the description tail is now a real activity, and the link
              is readable from both ends. An interaction that already has one
              says so instead of offering to create a second.
            */}
            <div className="mt-1">
              {interaction.followUpActivityId ? (
                <Link
                  to="/calendar"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <CalendarPlus size={12} aria-hidden="true" />
                  {S.activity.linkedActivity}
                </Link>
              ) : (
                onFollowUp && (
                  <button
                    type="button"
                    onClick={() => onFollowUp(interaction)}
                    className="inline-flex items-center gap-1 text-xs text-text-muted
                               hover:text-primary"
                  >
                    <CalendarPlus size={12} aria-hidden="true" />
                    {S.activity.createdFromInteraction}
                  </button>
                )
              )}
            </div>
          </li>
        ))}
      </ol>

      {hasMore && (
        <div className="mt-5 flex justify-center">
          <Button onClick={() => setPageSize((size) => size + TIMELINE_PAGE_SIZE)}>
            {loading ? <Spinner /> : S.interaction.loadMore}
          </Button>
        </div>
      )}
    </Card>
  );
}
